import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useNavigate } from "react-router-dom";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import ExpandableText from "../../components/common/ExpandableText";
import ButtonCreate from "../../components/drawer/ButtonCreate";
import ButtonEdit from "../../components/drawer/ButtonEdit";
import ShadowCard from "../../components/common/ShadowCard";
import {
  getAllBlogFAQ,
  deleteBlogFAQ,
  ActiveInactiveBlogFAQ,
} from "./services/blog.services";
import { setBlogFAQID, clearBlogFAQID } from "./blogSlice";

interface BlogFAQSectionProps {
  onBack: () => void;
}

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const BlogFAQSection: React.FC<BlogFAQSectionProps> = ({ onBack }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const BlogID = useAppSelector((state) => state.blog.BlogID);

  useEffect(() => {
    if (BlogID) {
      dispatch(getAllBlogFAQ(BlogID));
    }
    return () => {
      dispatch(closeDialog());
    };
  }, [BlogID]);

  return (
    <>
      <DeleteBlogFAQDialog />
      <ShadowCard style={{ padding: 12 }}>
        <GridLayout cols={[{ width: "100%" }]}>
          <GridLayoutItem
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "4px 0px 20px",
            }}
          >
            <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
              Blog FAQ List
            </Typography.h4>
            <ButtonCreate
              label="Add New FAQ"
              drawerName="blogFaqForm"
              drawerWidth={500}
            />
          </GridLayoutItem>
          <GridLayoutItem>
            <BlogFAQGridComponent />
          </GridLayoutItem>
        </GridLayout>

        <div style={{ textAlign: "end", marginTop: 20 }}>
          <Button
            type="button"
            fillMode="outline"
            themeColor="primary"
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            type="button"
            fillMode="solid"
            themeColor="primary"
            style={{ marginLeft: 10 }}
            onClick={() => navigate("/blog")}
          >
            Finish
          </Button>
        </div>
      </ShadowCard>
    </>
  );
};

const BlogFAQGridComponent: React.FC = () => {
  const dispatch = useAppDispatch();
  const BlogID = useAppSelector((state) => state.blog.BlogID);
  const BlogFAQList = useAppSelector((state) => state.blog.BlogFAQList);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? BlogFAQList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleOpenDeleteDialog = (ID: number) => {
    dispatch(openDialog("deleteBlogFAQ"));
    dispatch(setBlogFAQID(ID));
  };

  const MyEditCommandCell = (props: any) => {
    return (
      <td
        {...props.tdProps}
        style={props.tdProps.style}
        className={`action-td ${props.className}`}
      >
        <ButtonEdit
          ID={props.dataItem.ID}
          drawerName="blogFaqForm"
          tooltipTitle="Edit"
          drawerWidth={500}
        />
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Delete"
          className="color-red"
          onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
          icon={MdDelete}
          iconClassName="absolute-position"
          iconStyle={{ fontSize: "24px" }}
        />
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 3 || props.index === 4 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInactiveCommandCell = (props: any) => {
    const [IsActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    useEffect(() => {
      setIsActive(props.dataItem.IsActive);
    }, [props.dataItem.IsActive]);

    const handleActiveInactive = () => {
      const nextActive = !IsActive;
      setIsActive(nextActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: nextActive,
      };
      dispatch(ActiveInactiveBlogFAQ(data)).then(() => {
        if (BlogID) {
          dispatch(getAllBlogFAQ(BlogID));
        }
      });
    };

    return (
      <td
        {...props.tdProps}
        style={{ textAlign: "center", ...props.tdProps.style }}
        className={`action-td ${props.className}`}
      >
        <Switch
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInactive}
          size="small"
        />
      </td>
    );
  };

  return (
    <>
      {BlogFAQList && BlogFAQList.length ? (
        <KendoGrid
          className="responsive-table"
          style={{ maxHeight: "calc(100vh - 200px)" }}
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(BlogFAQList, filter).slice(
                  page.skip,
                  page.take + page.skip,
                )
              : BlogFAQList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={BlogFAQList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="SequenceNo"
            title="Seq. No."
            width={50}
            filterable={false}
          />
          <Column field="Question" title="Question" width={250} />
          <Column
            field="Answer"
            title="Answer"
            width={300}
            cells={{
              data: (props) => <ExpandableText text={props.dataItem.Answer} />,
            }}
          />
          <Column
            field="ID"
            title="Status"
            cells={{ data: MyActiveInactiveCommandCell }}
            width={"50px"}
            filterable={false}
            locked={true}
          />
          <Column
            field="ID"
            title="Actions"
            cells={{ data: MyEditCommandCell }}
            width={"80px"}
            filterable={false}
            locked={true}
          />
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteBlogFAQDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const BlogID = useAppSelector((state) => state.blog.BlogID);
  const BlogFAQID = useAppSelector((state) => state.blog.BlogFAQID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteBlogFAQ(ID));
      if (response.meta.requestStatus === "fulfilled") {
        if (BlogID) {
          dispatch(getAllBlogFAQ(BlogID));
        }
        dispatch(closeDialog());
        dispatch(clearBlogFAQID());
      } else {
        dispatch(closeDialog());
        dispatch(clearBlogFAQID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteBlogFAQ" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Blog FAQ?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this FAQ?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(BlogFAQID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default BlogFAQSection;
